import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { Share2, RotateCcw, Trophy, Send } from "lucide-react";
import { Header } from "@/components/Header";
import { Meters } from "@/components/Meters";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useEndActions } from "@/hooks/useEndActions";
import { termLength } from "@/lib/engine";

const StatBox = ({ label, children, testId }) => (
  <div className="panel p-4">
    <div className="text-[10px] uppercase tracking-widest text-[var(--text-light)]/60">{label}</div>
    <div className="font-display font-black text-3xl" data-testid={testId}>{children}</div>
  </div>
);

export default function EndScreen() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { state } = useLocation();
  if (!state) return <Navigate to="/" replace />;
  return <EndContent state={state} t={t} lang={lang} navigate={navigate} />;
}

const EndContent = ({ state, t, lang, navigate }) => {
  const { user } = useAuth();
  const { meters, turn, reason, survived, score, titleKey } = state;
  const { years, months } = termLength(turn);
  const title = t.titles[titleKey];
  const { nick, setNick, sent, busy, submit, share } = useEndActions({ t, lang, score, turn, titleKey, reason, meters, years, months, title, defaultNick: user?.name });

  return (
    <div className="max-w-md mx-auto min-h-dvh px-5 pb-6 flex flex-col" data-testid="end-screen">
      <Header />
      <div className="flex-1 flex flex-col gap-5 py-4">
        <div className="rise">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[var(--gold)]" data-testid="end-status">{survived ? t.termComplete : t.termOver}</div>
          <div className="text-xs uppercase tracking-widest text-[var(--text-light)]/50 mt-4">{t.yourTitle}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl leading-[0.95] tracking-tight text-[var(--gold-bright)]" data-testid="end-title">{title}</h1>
          <p className="mt-3 text-base leading-relaxed text-[var(--text-light)]/85" data-testid="end-reason">{t.reasons[reason]}</p>
        </div>
        <div className="rise d1 grid grid-cols-2 gap-3">
          <StatBox label={t.score} testId="end-score"><span className="text-[var(--gold)]">{score}</span></StatBox>
          <StatBox label={t.served} testId="end-served">
            {years}<span className="text-sm font-bold ml-1">{t.years}</span> {months}<span className="text-sm font-bold ml-1">{t.months}</span>
          </StatBox>
        </div>
        <div className="rise d2 panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-light)]/60 mb-3">{t.finalMeters}</div>
          <Meters meters={meters} />
        </div>
        <div className="rise d3 panel p-4 flex flex-col gap-2">
          {user && <div className="text-[11px] text-[var(--text-light)]/60" data-testid="signed-in-hint">{t.signedInHint}</div>}
          <div className="flex gap-2">
            <input value={nick} onChange={(e) => setNick(e.target.value)} maxLength={24} disabled={sent} placeholder={t.nickname} className="flex-1 bg-black/30 border border-[var(--kg-red-border)] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[var(--gold)] transition-colors" data-testid="nickname-input" />
            <button onClick={submit} disabled={sent || busy || !nick.trim()} className="btn-gold px-4 py-2.5 text-sm flex items-center gap-1" data-testid="submit-score-btn"><Send size={14} /> {sent ? "✓" : t.submit}</button>
          </div>
        </div>
        <div className="rise d4 grid grid-cols-3 gap-3">
          <button onClick={share} className="btn-ghost py-3 text-sm flex items-center justify-center gap-1" data-testid="share-btn"><Share2 size={16} /> {t.share}</button>
          <button onClick={() => navigate("/leaderboard")} className="btn-ghost py-3 text-sm flex items-center justify-center gap-1" data-testid="end-leaderboard-btn"><Trophy size={16} /></button>
          <button onClick={() => navigate("/play")} className="btn-gold py-3 text-sm flex items-center justify-center gap-1" data-testid="play-again-btn"><RotateCcw size={16} /> {t.playAgain}</button>
        </div>
      </div>
    </div>
  );
};
